<script setup>
import { ref } from "vue"
import { useMutation } from "@vue/apollo-composable"
import ValidationErrors from "@/components/common/ValidationErrors.vue"
import ValidationErrorsClass from "@/assets/js/ValidationErrors.js"
import FORGOT_PASSWORD_MUTATION from "@/graphql/Auth/ForgotPasswordMutation.gql"


const userEmail = ref(null)
const validationErrors = ref(null)
const successMessage = ref(null)

const { mutate: forgotPasswordMutation, loading: submitIsLoading } =
  useMutation(FORGOT_PASSWORD_MUTATION)

const submit = () => {
  successMessage.value = null
  validationErrors.value = null
  forgotPasswordMutation({
    input: {
      email: userEmail.value,
    },
  })
    .then(() => {
      successMessage.value = "Password reset has been sent."
    })
    .catch(error => {
      console.error("Error during mutation", error)
      let errors = new ValidationErrorsClass()
      errors.extractErrorsFrom(error)
      validationErrors.value = errors.errors
    })
}
</script>

<template>
  <div
    class="flex h-screen flex-1 flex-col items-center justify-center px-6 py-12 lg:px-8 overflow-hidden relative z-100"
  >
    <div class="mx-auto max-w-xs md:max-w-sm">
      <img
        class="mx-auto h-14 w-auto pr-4 mb-8"
        src="@/assets/images/ytz-logo.svg"
        alt="YTZ company logo"
      />
    </div>

    <div class="mt-10 mx-auto w-full max-w-xs md:max-w-sm">
      <!-- Use @submit.prevent on the form to prevent default behavior -->
      <form class="space-y-4" v-on:submit.prevent="submit">
        <div>
          <label for="email" class="block text-sm">
            Account Email
            <span 
              class="text-info ml-1"
              v-tooltip="'Enter the email address associated with your account to reset your password'"
              >
              <v-icon
                class="mr-2 text-accent"
                name="bi-info-circle"
                width="1em"
                height="1em"
                scale="1"
              />
            </span>
          </label>
          <div class="mt-2">
            <input
              v-model="userEmail"
              id="email"
              name="email"
              type="email"
              autocomplete="email"
              class="!text-base-content"
              required
            />
          </div>
        </div>
        <div>
          <XButton
            @click.prevent="submit"
            color="primary"
            type="submit"
            class="w-full"
            :isLoading="submitIsLoading"
          >
            Reset Password
          </XButton>
        </div>
      </form>

      <div v-if="successMessage" class="mt-3">
        <Alert
          type="success"
          :isShowing="successMessage"
          :alertMessage="successMessage"
        />
      </div>

      <ValidationErrors
        v-if="validationErrors"
        :errors="validationErrors"
        bannerClass="mt-3"
        variant="error"
        :maxErrors="1"
      />

      <p class="mt-10 text-center text-sm">
        Already have an account?

        <router-link
          :to="{ name: 'Login' }"
          class="font-semibold leading-6 text-accent hover:text-accent/80"
        >
          Login Now
        </router-link>
      </p>
    </div>
  </div>
</template>

<style scoped>
input[name="email"] {
  @apply block w-full rounded-md border-0 px-2.5 py-1.5 placeholder:text-base-300/40 focus:ring-2 focus:ring-accent outline-none sm:text-sm sm:leading-6 text-base-300;
}
</style>
